import React, { useEffect, useState } from "react";
import { Bar, BarChart, Tooltip, XAxis, YAxis, ResponsiveContainer, LabelList, Cell } from "recharts";
import { useSession } from "../../../SessionContext";
import { ScreenConfig } from "../Sequences";
import { useLanguage } from "../../../../context/LanguageContext";
import { DecoyEffect } from "../../../../translations/experiments/DecoyEffect";
import { colors, commonStyles } from "../../../../components/colors";

// Use PH/corporate chart colors
const pommesColors = [colors.lichtBlau, colors.ocker, colors.sienna];
const drinkColors = [colors.darkGreen, colors.brillantBlue, colors.ocker];

// Unified card styles
const cardStyle: React.CSSProperties = {
    ...commonStyles.card,
    margin: 0,
    minWidth: 470,
    maxWidth: 650,
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    minHeight: 250
};

const resultMessageStyle: React.CSSProperties = {
    ...commonStyles.card,
    marginTop: 32,
    textAlign: "center",
    fontSize: 17,
    background: colors.backgroundLight,
    maxWidth: 650,
    marginLeft: "auto",
    marginRight: "auto",
};

const chartBlockStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "row",
    gap: 24,
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 8
};

const chartStyle: React.CSSProperties = {
    minWidth: 180,
    maxWidth: 220,
    flex: 1
};

// Custom Tooltip with percentages
const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
        // Calculate total from all bars for percentage
        const allBars = payload[0].payload && payload[0].payload._all ? payload[0].payload._all : undefined;
        const value = payload[0].value;
        let total = 0;
        if (allBars && Array.isArray(allBars)) {
            total = allBars.reduce((acc, d) => acc + (typeof d.Anzahl === "number" ? d.Anzahl : 0), 0);
        }
        let percentage = total > 0 ? Math.round((value / total) * 100) : 0;
        return (
            <div style={{
                background: colors.white,
                border: `1px solid ${colors.silver}`,
                padding: 12,
                borderRadius: 8,
                fontSize: 14,
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)"
            }}>
                <div style={{
                    fontWeight: 600,
                    marginBottom: 6,
                    color: colors.brillantBlue
                }}>
                    {label}
                </div>
                <div style={{ color: payload[0].color || colors.textMedium }}>
                    {value}{total ? ` (${percentage}%)` : ""}
                </div>
            </div>
        );
    }
    return null;
};

function getFriesTextGr1(choice?: number, t?: any) {
    switch (choice) {
        case 1: return t.optionSmall;
        case 2: return t.optionLarge;
        default: return "-";
    }
}
function getDrinkTextGr1(choice?: number, t?: any) {
    switch (choice) {
        case 1: return t.optionSmall;
        case 2: return t.optionMedium;
        case 3: return t.optionLarge;
        default: return "-";
    }
}
function getFriesTextGr2(choice?: number, t?: any) {
    switch (choice) {
        case 1: return t.optionSmall;
        case 2: return t.optionMedium;
        case 3: return t.optionLarge;
        default: return "-";
    }
}
function getDrinkTextGr2(choice?: number, t?: any) {
    switch (choice) {
        case 1: return t.optionSmall;
        case 2: return t.optionLarge;
        default: return "-";
    }
}

export const ResultScreenComponent = () => {
    const { getGroupData, userData } = useSession();
    const { language } = useLanguage();
    const txt = DecoyEffect[language].screens;

    const [friesData1, setFriesData1] = useState<any[]>([]);
    const [drinkData1, setDrinkData1] = useState<any[]>([]);
    const [friesData2, setFriesData2] = useState<any[]>([]);
    const [drinkData2, setDrinkData2] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    // User choices
    let userFriesChoice: number | undefined = undefined;
    let userDrinkChoice: number | undefined = undefined;
    let userFriesChoice2: number | undefined = undefined;
    let userDrinkChoice2: number | undefined = undefined;

    if (userData && userData.DecoyEffect) {
        const obj = userData.DecoyEffect;
        if (obj["0"]?.selections?.length > 0)
            userFriesChoice = obj["0"].selections[obj["0"].selections.length - 1].choice;
        if (obj["1"]?.selections?.length > 0)
            userDrinkChoice = obj["1"].selections[obj["1"].selections.length - 1].choice;
        if (obj["2"]?.selections?.length > 0)
            userFriesChoice2 = obj["2"].selections[obj["2"].selections.length - 1].choice;
        if (obj["3"]?.selections?.length > 0)
            userDrinkChoice2 = obj["3"].selections[obj["3"].selections.length - 1].choice;
    }

    // Group
    let userGroup: number | null = null;
    let pommesLabel: string = "";
    let drinkLabel: string = "";

    if (userFriesChoice !== undefined && userDrinkChoice !== undefined) {
        userGroup = 1;
        pommesLabel = getFriesTextGr1(userFriesChoice, txt.fries1);
        drinkLabel = getDrinkTextGr1(userDrinkChoice, txt.drink1);
    } else if (userFriesChoice2 !== undefined && userDrinkChoice2 !== undefined) {
        userGroup = 2;
        pommesLabel = getFriesTextGr2(userFriesChoice2, txt.fries2);
        drinkLabel = getDrinkTextGr2(userDrinkChoice2, txt.drink2);
    }

    useEffect(() => {
        setLoading(true);
        getGroupData().then(data => {
            let fries1Counts = [0, 0];
            let drink1Counts = [0, 0, 0];
            let fries2Counts = [0, 0, 0];
            let drink2Counts = [0, 0];

            data?.forEach((dat: any) => {
                const obj = dat.DecoyEffect;
                if (obj) {
                    // Gruppe 1
                    if (obj["0"]?.selections?.length > 0) {
                        const choice = obj["0"].selections[obj["0"].selections.length - 1].choice;
                        if (choice === 1) fries1Counts[0]++;
                        if (choice === 2) fries1Counts[1]++;
                    }
                    if (obj["1"]?.selections?.length > 0) {
                        const choice = obj["1"].selections[obj["1"].selections.length - 1].choice;
                        if (choice === 1) drink1Counts[0]++;
                        if (choice === 2) drink1Counts[1]++;
                        if (choice === 3) drink1Counts[2]++;
                    }
                    // Gruppe 2
                    if (obj["2"]?.selections?.length > 0) {
                        const choice = obj["2"].selections[obj["2"].selections.length - 1].choice;
                        if (choice === 1) fries2Counts[0]++;
                        if (choice === 2) fries2Counts[1]++;
                        if (choice === 3) fries2Counts[2]++;
                    }
                    if (obj["3"]?.selections?.length > 0) {
                        const choice = obj["3"].selections[obj["3"].selections.length - 1].choice;
                        if (choice === 1) drink2Counts[0]++;
                        if (choice === 2) drink2Counts[1]++;
                    }
                }
            });

            // Add _all for custom tooltip to have access to totals
            setFriesData1([
                { name: txt.fries1.optionSmall, Anzahl: fries1Counts[0], color: pommesColors[0], _all: null },
                { name: txt.fries1.optionLarge, Anzahl: fries1Counts[1], color: pommesColors[2], _all: null }
            ].map((d, _, arr) => ({ ...d, _all: arr })));
            setDrinkData1([
                { name: txt.drink1.optionSmall, Anzahl: drink1Counts[0], color: drinkColors[0], _all: null },
                { name: txt.drink1.optionMedium, Anzahl: drink1Counts[1], color: drinkColors[1], _all: null },
                { name: txt.drink1.optionLarge, Anzahl: drink1Counts[2], color: drinkColors[2], _all: null }
            ].map((d, _, arr) => ({ ...d, _all: arr })));
            setFriesData2([
                { name: txt.fries2.optionSmall, Anzahl: fries2Counts[0], color: pommesColors[0], _all: null },
                { name: txt.fries2.optionMedium, Anzahl: fries2Counts[1], color: pommesColors[1], _all: null },
                { name: txt.fries2.optionLarge, Anzahl: fries2Counts[2], color: pommesColors[2], _all: null }
            ].map((d, _, arr) => ({ ...d, _all: arr })));
            setDrinkData2([
                { name: txt.drink2.optionSmall, Anzahl: drink2Counts[0], color: drinkColors[0], _all: null },
                { name: txt.drink2.optionLarge, Anzahl: drink2Counts[1], color: drinkColors[2], _all: null }
            ].map((d, _, arr) => ({ ...d, _all: arr })));
            setLoading(false);
        }).catch(error => {
            setLoading(false);
            console.error("Error fetching group data:", error);
        });
    }, [getGroupData, txt]);

    const headline: string =
        typeof txt.results.headline === "string" && txt.results.headline
            ? txt.results.headline
            : "Wie beeinflusst die zusätzliche Option die Auswahl?";

    if (loading) {
        return <div style={{
            textAlign: "center",
            marginTop: 50,
            fontSize: 17,
            color: colors.brillantBlue
        }}>
            {txt.results.loading}
        </div>;
    }

    return (
        <div className="experiment-container" style={commonStyles.container}>
            <h2 style={{
                ...commonStyles.heading,
                margin: "0 0 24px 0",
                fontSize: 28,
                fontWeight: 700
            }}>
                {headline}
            </h2>
            <div style={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "center",
                gap: 48,
                marginTop: 28
            }}>
                {/* Gruppe 1 */}
                <div style={cardStyle}>
                    <h3 style={{
                        ...commonStyles.heading,
                        textAlign: "center",
                        fontSize: 20,
                        marginBottom: 12
                    }}>
                        {txt.results.group1Title}
                    </h3>
                    <div style={chartBlockStyle}>
                        <div style={chartStyle}>
                            <div style={{ fontWeight: 500, marginBottom: 2, textAlign: "center" }}>{txt.results.pommesChoice}</div>
                            <ResponsiveContainer width="100%" height={110}>
                                <BarChart data={friesData1}>
                                    <XAxis dataKey="name" tick={{ fontSize: 13 }} />
                                    <YAxis allowDecimals={false} />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Bar dataKey="Anzahl" barSize={28}>
                                        {friesData1.map((entry, idx) => (
                                            <Cell key={entry.name} fill={entry.color} />
                                        ))}
                                        <LabelList dataKey="Anzahl" position="top" fontSize={13} />
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                        <div style={chartStyle}>
                            <div style={{ fontWeight: 500, marginBottom: 2, textAlign: "center" }}>{txt.results.drinkChoice}</div>
                            <ResponsiveContainer width="100%" height={110}>
                                <BarChart data={drinkData1}>
                                    <XAxis dataKey="name" tick={{ fontSize: 13 }} />
                                    <YAxis allowDecimals={false} />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Bar dataKey="Anzahl" barSize={28}>
                                        {drinkData1.map((entry, idx) => (
                                            <Cell key={entry.name} fill={entry.color} />
                                        ))}
                                        <LabelList dataKey="Anzahl" position="top" fontSize={13} />
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>
                {/* Gruppe 2 */}
                <div style={cardStyle}>
                    <h3 style={{
                        ...commonStyles.heading,
                        textAlign: "center",
                        fontSize: 20,
                        marginBottom: 12
                    }}>
                        {txt.results.group2Title}
                    </h3>
                    <div style={chartBlockStyle}>
                        <div style={chartStyle}>
                            <div style={{ fontWeight: 500, marginBottom: 2, textAlign: "center" }}>{txt.results.pommesChoice}</div>
                            <ResponsiveContainer width="100%" height={110}>
                                <BarChart data={friesData2}>
                                    <XAxis dataKey="name" tick={{ fontSize: 13 }} />
                                    <YAxis allowDecimals={false} />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Bar dataKey="Anzahl" barSize={28}>
                                        {friesData2.map((entry, idx) => (
                                            <Cell key={entry.name} fill={entry.color} />
                                        ))}
                                        <LabelList dataKey="Anzahl" position="top" fontSize={13} />
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                        <div style={chartStyle}>
                            <div style={{ fontWeight: 500, marginBottom: 2, textAlign: "center" }}>{txt.results.drinkChoice}</div>
                            <ResponsiveContainer width="100%" height={110}>
                                <BarChart data={drinkData2}>
                                    <XAxis dataKey="name" tick={{ fontSize: 13 }} />
                                    <YAxis allowDecimals={false} />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Bar dataKey="Anzahl" barSize={28}>
                                        {drinkData2.map((entry, idx) => (
                                            <Cell key={entry.name} fill={entry.color} />
                                        ))}
                                        <LabelList dataKey="Anzahl" position="top" fontSize={13} />
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>
            </div>
            <div style={resultMessageStyle}>
                {userGroup ? (
                    <span
                        dangerouslySetInnerHTML={{
                            __html: txt.results.yourGroup
                                .replace("{group}", String(userGroup))
                                .replace("{variant}", userGroup === 1 ? txt.results.group1Variant : txt.results.group2Variant)
                                .replace("{pommes}", pommesLabel)
                                .replace("{drink}", drinkLabel)
                        }}
                    />
                ) : (
                    txt.results.noChoices
                )}
            </div>
        </div>
    );
};

export const ResultScreen: ScreenConfig = {
    component: ResultScreenComponent,
    defaultResult: {},
    canContinueDefault: true,
};